import React, { useState, useEffect } from 'react';
import { independentAnalyticsService, independentQuotesService, independentItinerariesService, independentClientsService } from '../../services/independent';
import { TrendingUp, DollarSign, Users, FileText, Calendar, PieChart, BarChart3, Download } from 'lucide-react';

const IndependentAnalyticsEnhanced = () => {
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadAnalytics();
  }, [dateRange, startDate, endDate]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      
      // Load data from multiple sources
      const [quotes, itineraries, clients] = await Promise.all([
        independentQuotesService.getAll(),
        independentItinerariesService.getAll(),
        independentClientsService.getAll()
      ]);

      // Calculate analytics
      const quotesData = Array.isArray(quotes) ? quotes : quotes.data || [];
      const itinerariesData = Array.isArray(itineraries) ? itineraries : itineraries.data || [];
      const clientsData = Array.isArray(clients) ? clients : clients.data || [];

      // Filter by date range
      const now = new Date();
      const daysAgo = parseInt(dateRange);
      const filterDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);

      const recentQuotes = quotesData.filter(q => new Date(q.created_at) >= filterDate);
      const recentItineraries = itinerariesData.filter(i => new Date(i.created_at) >= filterDate);
      const recentClients = clientsData.filter(c => new Date(c.created_at) >= filterDate);

      // Calculate metrics
      const totalQuoteValue = recentQuotes.reduce((sum, q) => sum + parseFloat(q.total_price || 0), 0);
      const totalItineraryValue = recentItineraries.reduce((sum, i) => sum + parseFloat(i.total_price || 0), 0);
      const totalRevenue = recentItineraries.filter(i => i.status === 'completed').reduce((sum, i) => sum + parseFloat(i.total_price || 0), 0);
      const conversionRate = recentQuotes.length > 0 ? (recentItineraries.length / recentQuotes.length * 100).toFixed(1) : 0;

      setAnalytics({
        totalQuotes: recentQuotes.length,
        totalItineraries: recentItineraries.length,
        totalClients: recentClients.length,
        totalQuoteValue,
        totalItineraryValue,
        totalRevenue,
        conversionRate,
        avgQuoteValue: recentQuotes.length > 0 ? (totalQuoteValue / recentQuotes.length).toFixed(2) : 0,
        avgItineraryValue: recentItineraries.length > 0 ? (totalItineraryValue / recentItineraries.length).toFixed(2) : 0,
        quotesByStatus: {
          pending: recentQuotes.filter(q => q.status === 'pending').length,
          sent: recentQuotes.filter(q => q.status === 'sent').length,
          accepted: recentQuotes.filter(q => q.status === 'accepted').length,
          declined: recentQuotes.filter(q => q.status === 'declined').length
        },
        itinerariesByStatus: {
          confirmed: recentItineraries.filter(i => i.status === 'confirmed').length,
          pending: recentItineraries.filter(i => i.status === 'pending').length,
          completed: recentItineraries.filter(i => i.status === 'completed').length,
          cancelled: recentItineraries.filter(i => i.status === 'cancelled').length
        }
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = () => {
    const report = [
      ['Independent Agent Analytics Report'],
      ['Generated:', new Date().toLocaleString()],
      ['Date Range:', `Last ${dateRange} days`],
      [''],
      ['Key Metrics'],
      ['Total Quotes', analytics.totalQuotes],
      ['Total Itineraries', analytics.totalItineraries],
      ['Total Clients', analytics.totalClients],
      ['Quote Value', `$${analytics.totalQuoteValue?.toLocaleString()}`],
      ['Itinerary Value', `$${analytics.totalItineraryValue?.toLocaleString()}`],
      ['Revenue', `$${analytics.totalRevenue?.toLocaleString()}`],
      ['Conversion Rate', `${analytics.conversionRate}%`],
      ['Avg Quote Value', `$${analytics.avgQuoteValue}`],
      ['Avg Itinerary Value', `$${analytics.avgItineraryValue}`],
      [''],
      ['Quote Status Breakdown'],
      ['Pending', analytics.quotesByStatus?.pending],
      ['Sent', analytics.quotesByStatus?.sent],
      ['Accepted', analytics.quotesByStatus?.accepted],
      ['Declined', analytics.quotesByStatus?.declined],
      [''],
      ['Itinerary Status Breakdown'],
      ['Confirmed', analytics.itinerariesByStatus?.confirmed],
      ['Pending', analytics.itinerariesByStatus?.pending],
      ['Completed', analytics.itinerariesByStatus?.completed],
      ['Cancelled', analytics.itinerariesByStatus?.cancelled]
    ];

    const csv = report.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-1">Track your business performance and metrics</p>
        </div>
        <div className="flex space-x-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
          <button
            onClick={exportReport}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
          >
            <Download size={18} />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Quotes</p>
              <p className="text-2xl font-bold text-gray-900">{analytics.totalQuotes}</p>
              <p className="text-xs text-gray-500 mt-1">Last {dateRange} days</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="text-blue-600" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Itineraries</p>
              <p className="text-2xl font-bold text-green-600">{analytics.totalItineraries}</p>
              <p className="text-xs text-gray-500 mt-1">Last {dateRange} days</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Calendar className="text-green-600" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">New Clients</p>
              <p className="text-2xl font-bold text-purple-600">{analytics.totalClients}</p>
              <p className="text-xs text-gray-500 mt-1">Last {dateRange} days</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Users className="text-purple-600" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Conversion Rate</p>
              <p className="text-2xl font-bold text-orange-600">{analytics.conversionRate}%</p>
              <p className="text-xs text-gray-500 mt-1">Quotes to Itineraries</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="text-orange-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total Quote Value</p>
              <p className="text-3xl font-bold">${analytics.totalQuoteValue?.toLocaleString()}</p>
              <p className="text-blue-100 text-xs mt-1">Avg: ${analytics.avgQuoteValue}</p>
            </div>
            <DollarSign size={32} className="text-blue-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Total Itinerary Value</p>
              <p className="text-3xl font-bold">${analytics.totalItineraryValue?.toLocaleString()}</p>
              <p className="text-green-100 text-xs mt-1">Avg: ${analytics.avgItineraryValue}</p>
            </div>
            <DollarSign size={32} className="text-green-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Total Revenue</p>
              <p className="text-3xl font-bold">${analytics.totalRevenue?.toLocaleString()}</p>
              <p className="text-purple-100 text-xs mt-1">Completed trips</p>
            </div>
            <DollarSign size={32} className="text-purple-200" />
          </div>
        </div>
      </div>

      {/* Status Breakdowns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Quote Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Quote Status</h2>
            <PieChart className="text-gray-400" size={20} />
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Pending</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">{analytics.quotesByStatus?.pending || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Sent</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">{analytics.quotesByStatus?.sent || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Accepted</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">{analytics.quotesByStatus?.accepted || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Declined</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">{analytics.quotesByStatus?.declined || 0}</span>
            </div>
          </div>
        </div>

        {/* Itinerary Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Itinerary Status</h2>
            <BarChart3 className="text-gray-400" size={20} />
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Confirmed</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">{analytics.itinerariesByStatus?.confirmed || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Pending</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">{analytics.itinerariesByStatus?.pending || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Completed</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">{analytics.itinerariesByStatus?.completed || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Cancelled</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">{analytics.itinerariesByStatus?.cancelled || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Insights */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Performance Insights</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-600 font-medium mb-1">Average Quote Value</p>
            <p className="text-2xl font-bold text-blue-900">${analytics.avgQuoteValue}</p>
            <p className="text-xs text-blue-600 mt-1">Per quote created</p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <p className="text-sm text-green-600 font-medium mb-1">Average Itinerary Value</p>
            <p className="text-2xl font-bold text-green-900">${analytics.avgItineraryValue}</p>
            <p className="text-xs text-green-600 mt-1">Per confirmed trip</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <p className="text-sm text-purple-600 font-medium mb-1">Quote to Itinerary</p>
            <p className="text-2xl font-bold text-purple-900">{analytics.conversionRate}%</p>
            <p className="text-xs text-purple-600 mt-1">Conversion rate</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndependentAnalyticsEnhanced;
